import {
	faChevronLeft,
	faChevronRight,
	faCompress,
	faExpand,
	faImage,
	faSpinner,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useThemeColors } from '../../providers/ThemeProvider';
import { Icon } from '../base/Icon';
import { Text } from '../base/Text';

export interface ImageViewerModalProps {
	isOpen: boolean;
	onClose: () => void;
	images: Array<{
		url: string;
		title?: string;
		sortOrder?: number;
	}>;
	initialIndex?: number;
	itemName?: string;
	/** Hide the thumbnail strip (useful for lazy-loaded images) */
	hideThumbnails?: boolean;
	/** Callback when user navigates to a different image index */
	onIndexChange?: (index: number) => void;
}

export function ImageViewerModal({
	isOpen,
	onClose,
	images,
	initialIndex = 0,
	itemName,
	hideThumbnails = false,
	onIndexChange,
}: ImageViewerModalProps): React.ReactElement | null {
	const colors = useThemeColors();
	const [currentIndex, setCurrentIndex] = useState(initialIndex);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [imageLoading, setImageLoading] = useState(true);
	// Track which image URLs have been successfully loaded
	const [loadedUrls, setLoadedUrls] = useState<Set<string>>(new Set());

	useEffect(() => {
		setCurrentIndex(initialIndex);
	}, [initialIndex]);

	// Reset loaded URLs tracking only when modal opens fresh (not on every images update)
	useEffect(() => {
		if (isOpen) {
			setLoadedUrls(new Set());
		}
	}, [isOpen]);

	const hasMultipleImages = images.length > 1;
	const currentImage = images[currentIndex];
	const currentUrl = currentImage?.url ?? '';

	// Set loading when navigating to a new image, but only if it hasn't been loaded yet
	useEffect(() => {
		// Empty URL means still fetching - always show loading
		if (currentUrl === '') {
			setImageLoading(true);
		} else if (!loadedUrls.has(currentUrl)) {
			// New URL that hasn't been loaded yet
			setImageLoading(true);
		} else {
			// Already loaded this URL - no loading needed
			setImageLoading(false);
		}
	}, [currentIndex, currentUrl, loadedUrls]);

	const goToPrevious = useCallback((): void => {
		const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
		setCurrentIndex(newIndex);
		onIndexChange?.(newIndex);
	}, [currentIndex, images.length, onIndexChange]);

	const goToNext = useCallback((): void => {
		const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
		setCurrentIndex(newIndex);
		onIndexChange?.(newIndex);
	}, [currentIndex, images.length, onIndexChange]);

	const toggleFullscreen = (): void => {
		setIsFullscreen(!isFullscreen);
	};

	// Handle keyboard navigation
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleKeyDown = (e: KeyboardEvent): void => {
			switch (e.key) {
				case 'ArrowLeft':
					if (hasMultipleImages) {
						goToPrevious();
					}
					break;
				case 'ArrowRight':
					if (hasMultipleImages) {
						goToNext();
					}
					break;
				case 'Escape':
					onClose();
					break;
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return (): void => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, hasMultipleImages, onClose, goToPrevious, goToNext]);

	if (currentImage == null || !isOpen) {
		return null;
	}

	// Rendered into document.body so a card with its own stacking context (an
	// issue overview card, a list row) cannot draw its neighbors above the
	// full-screen viewer; `fixed z-50` alone only works at the top level.
	return createPortal(
		<>
			{/* Overlay */}
			{/*
			  Declared as a modal dialog for assistive technology, and because layers beneath
			  ask the page whether a dialog is open before acting on Escape: without the role,
			  closing this viewer with Escape also closed the Settings screen under it.
			*/}
			<div
				role="dialog"
				aria-modal="true"
				className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
				onClick={onClose}
			>
				{/* Modal content - prevent click through */}
				<div
					className="relative w-full h-full flex flex-col bg-black"
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					{/* Header */}
					<div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-black/50">
						<Text className="text-white text-lg font-medium">
							{itemName ?? 'Image'}
							{hasMultipleImages &&
								` (${currentIndex + 1} of ${images.length})`}
						</Text>
						<div className="flex gap-2">
							<button
								onClick={toggleFullscreen}
								className="p-2 rounded-lg hover:bg-white/10 transition-colors"
							>
								<Icon
									icon={isFullscreen ? faCompress : faExpand}
									size={20}
									color="white"
								/>
							</button>
							<button
								onClick={onClose}
								className="p-2 rounded-lg hover:bg-white/10 transition-colors"
							>
								<Icon icon={faXmark} size={24} color="white" />
							</button>
						</div>
					</div>

					{/* Image Container - use calc to account for header and thumbnail heights */}
					<div
						className="flex-1 flex items-center justify-center px-4"
						style={{
							height:
								hasMultipleImages && !hideThumbnails
									? 'calc(100vh - 72px - 120px)' // header + thumbnails
									: 'calc(100vh - 72px)', // just header (or no thumbnails)
							marginTop: '72px', // space for header
						}}
					>
						{/* Loading state - show spinner when fetching, placeholder icon when no URL */}
						{(imageLoading || currentUrl === '') && (
							<div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
								{currentUrl === '' ? (
									<>
										<Icon icon={faImage} size={64} className="text-subtle" />
										<div className="flex items-center gap-2 text-faint">
											<Icon
												icon={faSpinner}
												size={16}
												className="animate-spin"
											/>
											<span>Loading image...</span>
										</div>
									</>
								) : (
									<Icon
										icon={faSpinner}
										size={32}
										color={colors.values.primary}
										className="animate-spin"
									/>
								)}
							</div>
						)}

						{/* Main Image - only render when we have a URL */}
						{currentUrl !== '' && (
							<img
								src={currentUrl}
								alt={currentImage.title ?? itemName ?? 'Image'}
								className={`max-w-full max-h-full object-contain transition-opacity ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
								onLoad={() => {
									setImageLoading(false);
									// Track that this URL has been loaded
									setLoadedUrls((prev) => new Set(prev).add(currentUrl));
								}}
								onError={() => {
									setImageLoading(false);
								}}
							/>
						)}

						{/* Navigation Arrows - always visible for multiple images */}
						{hasMultipleImages && (
							<>
								<button
									onClick={goToPrevious}
									className="absolute left-4 p-3 rounded-full bg-black/50 hover:bg-black/70 transition-colors z-10"
								>
									<Icon icon={faChevronLeft} size={24} color="white" />
								</button>

								<button
									onClick={goToNext}
									className="absolute right-4 p-3 rounded-full bg-black/50 hover:bg-black/70 transition-colors z-10"
								>
									<Icon icon={faChevronRight} size={24} color="white" />
								</button>
							</>
						)}
					</div>

					{/* Thumbnail Strip (for multiple images, unless hidden) */}
					{hasMultipleImages && !hideThumbnails && (
						<div
							className="absolute bottom-0 left-0 right-0 bg-black/80 p-4 border-t border-success-tint"
							style={{ height: '120px' }}
						>
							<div className="flex justify-center gap-2 flex-wrap">
								{images.map((img, index) => (
									<button
										key={index}
										onClick={() => {
											setCurrentIndex(index);
										}}
										className={`border-2 rounded transition-colors ${
											index === currentIndex
												? 'border-white'
												: 'border-transparent'
										}`}
									>
										<img
											src={img.url}
											alt={`Thumbnail ${index + 1}`}
											className="w-16 h-16 object-cover rounded-sm"
										/>
									</button>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</>,
		document.body,
	);
}

export default ImageViewerModal;
